import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle2, Download, Loader2, AlertCircle, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

const PurchaseSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('checking'); // checking | paid | failed | expired
  const [message, setMessage] = useState('Confirming your payment…');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  const poll = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (data.payment_status === 'paid') {
        setStatus('paid');
        setMessage('Payment successful! Your E-Book is ready.');
        return;
      }
      if (data.status === 'expired') {
        setStatus('expired');
        setMessage('This checkout session has expired. Please try purchasing again.');
        return;
      }
      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus('failed');
        setMessage(
          "We couldn't confirm your payment in time. If you completed payment, please refresh — it can take a moment."
        );
        return;
      }
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    } catch (err) {
      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus('failed');
        setMessage('Error confirming payment. Please contact support.');
        return;
      }
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      setMessage('No checkout session found.');
      return;
    }
    poll();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId, poll]);

  const handleDownload = async () => {
    if (!sessionId) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const response = await axios.get(`${API}/ebook/download/${sessionId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Taking_Your_Power_Back.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // When responseType is 'blob', error bodies are also blobs — convert to text/JSON
      let detail = null;
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          try {
            detail = JSON.parse(text).detail;
          } catch {
            detail = text;
          }
        } catch {
          detail = null;
        }
      } else if (typeof data === 'object' && data) {
        detail = data.detail;
      }
      if (err?.response?.status === 503) {
        setDownloadError(
          "Your purchase is confirmed, but the E-Book file isn't uploaded yet. We'll email it to you shortly!"
        );
      } else {
        setDownloadError(detail || 'Download failed. Please try again or contact support.');
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-background flex items-center justify-center px-4 py-16" data-testid="purchase-success-page">
      <Card className="w-full max-w-xl p-8 md:p-12 card-shimmer text-center">
        {status === 'checking' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Loader2 className="w-14 h-14 mx-auto text-primary animate-spin mb-5" />
            <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
              Confirming your payment…
            </h1>
            <p className="text-muted-foreground" data-testid="status-message">{message}</p>
          </motion.div>
        )}

        {status === 'paid' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-16 h-16 mx-auto rounded-full bg-success/20 flex items-center justify-center mb-5">
              <CheckCircle2 className="w-10 h-10 text-success" />
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3" data-testid="success-heading">
              Thank you for your purchase!
            </h1>
            <p className="text-muted-foreground mb-8 leading-relaxed">
              Your copy of <span className="font-semibold text-foreground">Taking Your Power Back</span>{' '}
              is ready. Click below to download your E-Book.
            </p>

            <Button
              size="lg"
              className="btn-gold text-lg px-10 py-6 font-semibold rounded-full"
              onClick={handleDownload}
              disabled={downloading}
              data-testid="download-ebook-button"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Preparing…
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  Download E-Book (PDF)
                </>
              )}
            </Button>

            {downloadError && (
              <div className="mt-5 p-4 rounded-lg bg-warning/10 border border-warning/30 text-sm text-foreground text-left flex gap-2" data-testid="download-error">
                <AlertCircle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
                <span>{downloadError}</span>
              </div>
            )}

            <div className="mt-10 pt-6 border-t border-silver/30 space-y-5">
              {/* Free bonus: Power Planning Practice App */}
              <div className="rounded-lg border border-secondary/30 bg-secondary/5 p-5 text-left" data-testid="practice-app-bonus">
                <div className="flex items-start gap-3">
                  <div className="text-2xl shrink-0">🎁</div>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-widest text-secondary font-semibold mb-1">Your free bonus is unlocked</p>
                    <h3 className="font-display text-lg font-bold text-foreground mb-1">Power Planning Practice App</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                      Guided meditations, daily / weekly / monthly worksheets, and an evening check-in.
                      Check your inbox — we just emailed your magic link.
                    </p>
                    <Link to="/practice">
                      <Button size="sm" variant="outline" className="border-secondary/40 text-secondary hover:bg-secondary/10 gap-2" data-testid="practice-app-link">
                        Open Practice App →
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-3">
                  While you&apos;re here, explore the full E-Course experience:
                </p>
                <Link to="/chapters">
                  <Button variant="outline" className="gap-2">
                    <BookOpen className="w-4 h-4" />
                    Browse Chapters
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {(status === 'failed' || status === 'expired') && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center mb-5">
              <AlertCircle className="w-10 h-10 text-destructive" />
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
              Payment not confirmed
            </h1>
            <p className="text-muted-foreground mb-6" data-testid="status-message">{message}</p>
            <Link to="/ebook">
              <Button className="btn-teal" data-testid="back-to-landing-button">Back to E-Book Page</Button>
            </Link>
          </motion.div>
        )}
      </Card>
    </div>
  );
};

export default PurchaseSuccessPage;
export { PurchaseSuccessPage };
